import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen, Users, Video, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';

const courses = [
  {
    title: 'Pricing Your Art for Profit',
    description: 'Learn how to calculate true costs and set prices that ensure profitability.',
    format: 'Online',
    duration: '2 hours',
    icon: '💰',
  },
  {
    title: 'Social Media for Artisans',
    description: 'Build your brand and reach customers through effective social strategies.',
    format: 'Online',
    duration: '3 hours',
    icon: '📱',
  },
  {
    title: 'Event Success Strategies',
    description: 'Maximize your craft show experience from booth setup to customer engagement.',
    format: 'In-Person',
    duration: '4 hours',
    icon: '🎪',
  },
  {
    title: 'Business Basics for Creatives',
    description: 'Essential business skills including bookkeeping, taxes, and legal considerations.',
    format: 'Online',
    duration: '6 hours',
    icon: '📊',
  },
];

const learningFormats = [
  {
    icon: Video,
    title: 'Online Seminars',
    description: 'Learn at your own pace with on-demand video courses and live webinars.',
  },
  {
    icon: Users,
    title: 'In-Person Workshops',
    description: 'Hands-on learning experiences with direct mentor interaction.',
  },
  {
    icon: BookOpen,
    title: 'Resource Library',
    description: 'Access templates, guides, and tools to implement what you learn.',
  },
];

export default function UniversityPage() {
  return (
    <div className="page-transition" data-testid="university-page">
      <SEOHead page="university" />
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              <span>AMS University</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-semibold text-foreground mb-6">
              Learn & Grow
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              Our educational series of online and in-person seminars designed to teach 
              business skills to artisans. Turn your creative passion into a sustainable, 
              profitable business.
            </p>
            <Link to="/contact">
              <Button size="lg" className="rounded-full px-8 py-6 text-lg btn-shine" data-testid="university-cta">
                Explore Courses
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Learning Formats */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">How You'll Learn</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Multiple Ways to Grow
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {learningFormats.map((format, index) => (
              <motion.div
                key={format.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <format.icon className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-3">{format.title}</h3>
                <p className="text-muted-foreground">{format.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Catalog */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">What You'll Learn</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Featured Courses
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((course, index) => (
              <motion.div
                key={course.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-border rounded-2xl p-6 shadow-sm card-hover"
              >
                <div className="flex items-start gap-4">
                  <span className="text-4xl">{course.icon}</span>
                  <div className="flex-1">
                    <h3 className="text-xl font-serif font-semibold mb-2">{course.title}</h3>
                    <p className="text-muted-foreground mb-4">{course.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full">
                        {course.format}
                      </span>
                      <span className="text-muted-foreground">{course.duration}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Learn With Us */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-accent text-2xl">Why AMS University</span>
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2 mb-6">
                Learn from Those Who've Been There
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our instructors aren't just business experts—they're successful artisans 
                who understand the unique challenges of creative entrepreneurship.
              </p>
              <ul className="space-y-4">
                {[
                  'Curriculum designed specifically for artisans',
                  'Real-world case studies from successful makers',
                  'Actionable strategies you can implement immediately',
                  'Community support and peer networking',
                  'Ongoing resources and updates',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-500 to-primary rounded-2xl p-8 text-white"
            >
              <h3 className="text-2xl font-serif font-semibold mb-4">Ready to Level Up?</h3>
              <p className="text-white/80 mb-6">
                Join our next workshop and start building the business skills you need to thrive.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full" data-testid="university-enroll-cta">
                  Contact for Enrollment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
